import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeelingLevelSelector = ({ selectedLevel, onSelect }) => {
  const feelingLevels = [
    { id: 'good', label: 'Feeling Good', icon: 'Smile', color: 'text-success', bgColor: 'bg-success/10', description: 'No symptoms today' },
    { id: 'mild', label: 'Mild Symptoms', icon: 'Meh', color: 'text-warning', bgColor: 'bg-warning/10', description: 'Some discomfort' },
    { id: 'moderate', label: 'Moderate Symptoms', icon: 'Frown', color: 'text-error', bgColor: 'bg-error/10', description: 'Noticeable symptoms' },
    { id: 'severe', label: 'Severe Symptoms', icon: 'Angry', color: 'text-error', bgColor: 'bg-error/10', description: 'Significant impact' }
  ];

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          How are you feeling today?
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Select the option that best describes your current state
        </p>
      </div>

      {/* Helpful tip for first-time users */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Icon name="Lightbulb" size={20} className="text-teal-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-teal-900 mb-1">
              👋 First time here? Here's a quick tip:
            </p>
            <p className="text-xs text-teal-700">
              If you're feeling good with no symptoms, just tap the green button for a quick check-in. Otherwise, select your feeling level to provide more details.
            </p>
          </div>
        </div>
      </div>

      {/* One-Tap Feeling Good Button */}
      <div className="bg-gradient-to-r from-success/5 to-success/10 rounded-xl p-4 border-2 border-success/20">
        <Button
          variant="default"
          size="lg"
          onClick={() => onSelect('good')}
          className="w-full bg-success hover:bg-success/90 text-white shadow-lg"
          iconName="Smile"
          iconPosition="left"
        >
          <span className="flex items-center justify-center gap-2">
            <span className="text-lg font-bold">I'm Feeling Good Today!</span>
            <span className="text-sm opacity-90">(Quick Entry)</span>
          </span>
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-2">
          Tap here for a quick check-in if you have no symptoms
        </p>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or select your feeling level</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {feelingLevels?.map((level) => (
          <button
            key={level?.id}
            onClick={() => onSelect(level?.id)}
            className={`
              relative p-6 rounded-xl border-2 transition-all duration-200
              hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-3 focus:ring-ring
              ${
                selectedLevel === level?.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border bg-card hover:border-primary/50'
              }
            `}
            aria-label={`${level?.label}: ${level?.description}`}
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div className={`flex items-center justify-center w-16 h-16 rounded-full ${level?.bgColor}`}>
                <Icon name={level?.icon} size={32} className={level?.color} strokeWidth={2} />
              </div>
              <div>
                <h4 className="text-base md:text-lg font-heading font-bold text-foreground mb-1">
                  {level?.label}
                </h4>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {level?.description}
                </p>
              </div>
            </div>
            {selectedLevel === level?.id && (
              <div className="absolute top-3 right-3">
                <Icon name="CheckCircle2" size={20} color="var(--color-primary)" strokeWidth={2.5} />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FeelingLevelSelector;